import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, MessageCircle, Copy, Newspaper, ExternalLink, MapPin, CalendarDays } from "lucide-react";
import { toast } from "sonner";

// Partage d'un article d'actualité issu de la revue de presse locale.
export default function ProPressShare() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    api.get("/press-review?limit=60")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // URL affichée (page app) et URL de partage (page OG soignée) distinctes.
  const viewUrl = (p) => `${ORIGIN}/actu/${p.slug}`;
  const shareUrl = (p) => `${BACKEND}/api/actu-vitrine/${p.slug}`;
  const count = (p, channel) => { api.post(`/press-review/${p.slug}/share`, { channel }).catch(() => {}); };
  const fb = (p) => { count(p, "facebook"); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(p))}`, "_blank", "noopener,noreferrer"); };
  const wa = (p) => { count(p, "whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(`${p.title}\n${shareUrl(p)}`)}`, "_blank", "noopener,noreferrer"); };
  const copy = async (p) => { count(p, "copy"); try { await navigator.clipboard.writeText(shareUrl(p)); toast.success("Lien copié !"); } catch { toast.info(shareUrl(p)); } };

  return (
    <div data-testid="pro-press-share">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Newspaper className="text-[#E05638]" size={24} /> Partager un article d'actualité
      </h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        Relayez l'actualité locale de la revue de presse dans vos groupes Facebook, sur WhatsApp ou par lien. L'aperçu affiche l'image et le titre.
      </p>

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement…</div> :
       items.length === 0 ? <div className="mt-6 text-slate-500 text-sm">Aucune actualité disponible pour le moment.</div> :
       <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
         {items.map((p) => (
           <div key={p.id || p.slug} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`pro-press-article-${p.slug}`}>
             <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
               {p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /> :
                 <div className="w-full h-full grid place-items-center text-slate-300"><Newspaper size={32} /></div>}
             </div>
             <div className="p-4 flex-1 flex flex-col">
               <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                 {p.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.city}</span>}
                 {p.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> {new Date(p.published_at).toLocaleDateString("fr-FR")}</span>}
               </div>
               <h3 className="mt-1 font-heading font-bold text-slate-900 line-clamp-2">{p.title}</h3>
               {p.excerpt && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.excerpt}</p>}
               <a href={viewUrl(p)} target="_blank" rel="noreferrer" className="mt-2 text-xs text-blue-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`pro-press-article-open-${p.slug}`}>
                 Voir en ligne <ExternalLink size={11} />
               </a>
               <div className="mt-auto pt-3 border-t border-slate-100 flex gap-2">
                 <Button size="sm" onClick={() => fb(p)} className="bg-[#1877F2] hover:bg-[#1465d8] text-white flex-1" data-testid={`pro-press-article-fb-${p.slug}`}>
                   <Facebook size={14} className="mr-1" /> Facebook
                 </Button>
                 <Button size="sm" variant="outline" onClick={() => wa(p)} className="text-green-700 border-green-300" data-testid={`pro-press-article-wa-${p.slug}`}><MessageCircle size={14} /></Button>
                 <Button size="sm" variant="outline" onClick={() => copy(p)} data-testid={`pro-press-article-copy-${p.slug}`}><Copy size={14} /></Button>
               </div>
             </div>
           </div>
         ))}
       </div>}
    </div>
  );
}
